class RcMediaGallery extends HTMLElement {
  constructor() {
    super();
    this._onThumbClick = this._handleThumbClick.bind(this);
    this._onModalOpen = this._handleModalOpen.bind(this);
    this._onTouchStart = this._onTouchStart.bind(this);
    this._onTouchEnd = this._onTouchEnd.bind(this);
    this._onThumbsNavClick = this._handleThumbsNavClick.bind(this);
    this._touchX0 = null;
    this._index = 0;
    this._thumbNavPrev = null;
    this._thumbNavNext = null;
  }

  connectedCallback() {
    this._main = this.querySelector('[data-gallery-main]');
    this._thumbs = this.querySelector('[data-gallery-thumbs]');
    this._modalId = this.getAttribute('data-modal-id');
    this._lightboxFor = this.getAttribute('data-lightbox-for');

    this._slides = this._main
      ? [...this._main.querySelectorAll('[data-gallery-slide]')]
      : [];
    this._thumbButtons = this._thumbs
      ? [...this._thumbs.querySelectorAll('[data-gallery-thumb]')]
      : [];
    this._thumbNavPrev = this.querySelector('[data-gallery-thumbs-nav="prev"]');
    this._thumbNavNext = this.querySelector('[data-gallery-thumbs-nav="next"]');

    if (this._main) {
      this._main.addEventListener('touchstart', this._onTouchStart, { passive: true });
      this._main.addEventListener('touchend', this._onTouchEnd, { passive: true });
    }
    if (this._thumbs) {
      this._thumbs.addEventListener('click', this._onThumbClick);
    }
    if (this._thumbNavPrev || this._thumbNavNext) {
      this.addEventListener('click', this._onThumbsNavClick);
    }

    if (this._modalId) {
      const zoomBtn = this.querySelector('[data-gallery-lightbox-open]');
      if (zoomBtn) {
        zoomBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._emitOpenLightbox();
        });
      }
      this._main?.addEventListener('click', (e) => {
        if (e.target.closest('video, iframe, button, a')) return;
        this._emitOpenLightbox();
      });
    }

    if (this._lightboxFor) {
      window.addEventListener('modal-open', this._onModalOpen);
    }

    this._decorateThumbs();
    this.goToSlide(this._readInitialSlideIndex());
  }

  disconnectedCallback() {
    if (this._main) {
      this._main.removeEventListener('touchstart', this._onTouchStart);
      this._main.removeEventListener('touchend', this._onTouchEnd);
    }
    if (this._thumbs) {
      this._thumbs.removeEventListener('click', this._onThumbClick);
    }
    if (this._thumbNavPrev || this._thumbNavNext) {
      this.removeEventListener('click', this._onThumbsNavClick);
    }
    if (this._lightboxFor) window.removeEventListener('modal-open', this._onModalOpen);
  }

  _readInitialSlideIndex() {
    const found = this._slides.findIndex((el) =>
      el.classList.contains('gallery-slide--active')
    );
    return found >= 0 ? found : 0;
  }

  _onTouchStart(e) {
    if (e.touches.length !== 1) return;
    this._touchX0 = e.touches[0].clientX;
  }

  _onTouchEnd(e) {
    if (this._touchX0 == null) return;
    const x = e.changedTouches[0]?.clientX;
    if (x == null) {
      this._touchX0 = null;
      return;
    }
    const dx = x - this._touchX0;
    this._touchX0 = null;
    if (Math.abs(dx) < 48) return;
    if (dx < 0) this.goToSlide(this._index + 1);
    else this.goToSlide(this._index - 1);
  }

  _emitOpenLightbox() {
    if (!this._modalId) return;
    window.dispatchEvent(
      new CustomEvent('modal-open', {
        detail: { id: this._modalId, activeIndex: this._index },
      })
    );
  }

  _handleModalOpen(e) {
    const id = e?.detail?.id;
    if (!id || id !== this._lightboxFor) return;
    const idx =
      typeof e.detail.activeIndex === 'number' ? e.detail.activeIndex : 0;
    requestAnimationFrame(() => {
      this.goToSlide(idx);
      // Modal was display-hidden; second frame so thumb strip has real dimensions for centering.
      requestAnimationFrame(() => {
        this._scrollActiveThumbIntoView();
      });
    });
  }

  _handleThumbsNavClick(e) {
    const nav = e.target.closest('[data-gallery-thumbs-nav]');
    if (!nav || !this.contains(nav)) return;
    e.preventDefault();
    e.stopPropagation();
    const dir = nav.getAttribute('data-gallery-thumbs-nav');
    if (dir === 'prev') {
      this.goToSlide(this._index - 1);
    } else if (dir === 'next') {
      this.goToSlide(this._index + 1);
    }
  }

  _updateThumbsNavState() {
    const navs = [this._thumbNavPrev, this._thumbNavNext].filter(Boolean);
    if (!navs.length) return;
    if (!this._thumbs || this._slides.length < 2) {
      navs.forEach((el) => el.classList.add('thumb-nav--off'));
      return;
    }
    navs.forEach((el) => el.classList.remove('thumb-nav--off'));
    const last = this._slides.length - 1;
    if (this._thumbNavPrev) {
      this._thumbNavPrev.disabled = this._index <= 0;
    }
    if (this._thumbNavNext) {
      this._thumbNavNext.disabled = this._index >= last;
    }
  }

  _handleThumbClick(e) {
    const btn = e.target.closest('[data-gallery-thumb]');
    if (!btn || !this._thumbs?.contains(btn)) return;
    e.preventDefault();
    e.stopPropagation();
    const rawAttr = btn.getAttribute('data-slide-index');
    let idx =
      rawAttr != null && rawAttr !== '' ? parseInt(rawAttr, 10) : NaN;
    if (Number.isNaN(idx)) {
      idx = this._thumbButtons.indexOf(btn);
    }
    if (idx < 0) return;
    this.goToSlide(idx);
  }

  goToSlide(index) {
    if (!this._slides.length) return;
    const max = this._slides.length - 1;
    const i = Math.max(0, Math.min(index, max));
    this._index = i;

    this._slides.forEach((el, idx) => {
      const raw = el.getAttribute('data-slide-index');
      let j = raw != null && raw !== '' ? parseInt(raw, 10) : NaN;
      if (Number.isNaN(j)) j = idx;
      const on = j === i;
      el.classList.toggle('gallery-slide--active', on);
      el.classList.toggle('opacity-100', on);
      el.classList.toggle('z-10', on);
      el.classList.toggle('pointer-events-auto', on);
      el.classList.toggle('opacity-0', !on);
      el.classList.toggle('pointer-events-none', !on);
      el.classList.toggle('z-0', !on);
      el.setAttribute('aria-hidden', on ? 'false' : 'true');
    });

    this._updateThumbState();
    this._scrollActiveThumbIntoView();
    this._updateThumbsNavState();
  }

  _decorateThumbs() {
    this._thumbButtons.forEach((btn, i) => {
      btn.setAttribute('role', 'tab');
      if (!btn.hasAttribute('aria-label')) {
        btn.setAttribute('aria-label', `View image ${i + 1}`);
      }
    });
  }

  _thumbIndex(btn) {
    const raw = btn.getAttribute('data-slide-index');
    if (raw != null && raw !== '') {
      const n = parseInt(raw, 10);
      if (!Number.isNaN(n)) return n;
    }
    return this._thumbButtons.indexOf(btn);
  }

  _updateThumbState() {
    this._thumbButtons.forEach((btn) => {
      const active = this._thumbIndex(btn) === this._index
      if (active) {
        btn.setAttribute('data-active', 'true')
      } else {
        btn.removeAttribute('data-active');
      }
    });
  }

  _scrollActiveThumbIntoView() {
    if (!this._thumbs || !this._thumbButtons.length) return;
    const btn =
      this._thumbButtons.find((b) => this._thumbIndex(b) === this._index) ||
      null;
    if (!btn) return;
    const strip = this._thumbs;

    const canScrollX = strip.scrollWidth > strip.clientWidth;
    const canScrollY = strip.scrollHeight > strip.clientHeight;
    if (!canScrollX && !canScrollY) return;

    const stripRect = strip.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();

    if (canScrollX) {
      const deltaX =
        btnRect.left +
        btnRect.width / 2 -
        (stripRect.left + stripRect.width / 2);
      const maxLeft = strip.scrollWidth - strip.clientWidth;
      strip.scrollLeft = Math.max(
        0,
        Math.min(strip.scrollLeft + deltaX, maxLeft)
      );
    }

    if (canScrollY) {
      const deltaY =
        btnRect.top +
        btnRect.height / 2 -
        (stripRect.top + stripRect.height / 2);
      const maxTop = strip.scrollHeight - strip.clientHeight;
      strip.scrollTop = Math.max(
        0,
        Math.min(strip.scrollTop + deltaY, maxTop)
      );
    }
  }
}

if (!customElements.get('rc-media-gallery')) {
  customElements.define('rc-media-gallery', RcMediaGallery);
}
